// Email-related filters (system_sent_emails, email_templates, messaging_templates)

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const emailFilters: FilterRegistry = {
	systemEmailSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by subject, recipient, or message ID...',
			objectTable: 'email_record',
			listKeys: ['system_sent_emails'],
			fulltextFields: ['subject', 'messageId', 'fromEmail'],
		}),
		queryParamName: 'systemEmailSearch',
	},

	systemEmailStatus: {
		...createStatusFilter({
			label: 'Status',
			options: [
				{ id: 'sent', label: 'Sent' },
				{ id: 'failed', label: 'Failed' },
				{ id: 'bounced', label: 'Bounced' },
				{ id: 'complained', label: 'Complained' },
				{ id: 'pending', label: 'Pending' },
			],
			objectTable: 'email_record',
			fieldName: 'send_status',
			listKeys: ['system_sent_emails'],
			colorScheme: 'green',
		}),
		queryParamName: 'systemEmailStatus',
	},

	systemEmailSentDate: {
		...createDateRangeFilter({
			label: 'Sent Date',
			objectTable: 'email_record',
			dateField: 'sent_at',
			listKeys: ['system_sent_emails'],
		}),
		queryParamName: 'systemEmailSentDate',
		valueExtractor: {
			property: 'data',
		},
	},

	systemEmailTemplate: {
		...createEntityLookupFilter({
			label: 'Email Template',
			placeholder: 'Select templates...',
			apiEndpoint: '/email/templates',
			objectTable: 'email_record',
			fieldName: 'fk_email_template__id',
			listKeys: ['system_sent_emails'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'description',
			},
			colorScheme: 'purple',
		}),
		queryParamName: 'systemEmailTemplate',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	systemEmailHasAutomation: {
		type: 'single-select',
		label: 'Has Automation',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'systemEmailHasAutomation',
		multiple: false,
		valueExtractor: {
			property: 'id',
		},
		options: [
			{ id: 'true', label: 'Yes' },
			{ id: 'false', label: 'No' },
		],
		queryMapping: {
			objectTable: 'email_record',
			fieldName: 'fk__automation_step_execution__id',
			joinType: 'exists',
		},
		listKeys: ['system_sent_emails'],
	},

	emailTemplateKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name or description...',
			objectTable: 'email_template',
			listKeys: ['email_templates'],
			fulltextFields: ['name', 'description'],
		}),
		queryParamName: 'emailTemplateKeywords',
	},

	emailTemplateStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'emailTemplateStatus',
		multiple: false,
		valueExtractor: {
			property: 'id',
		},
		options: [
			{ id: 'true', label: 'Active' },
			{ id: 'false', label: 'Inactive' },
		],
		queryMapping: {
			objectTable: 'email_template',
			fieldName: 'is_active',
			joinType: 'direct',
		},
		listKeys: ['email_templates'],
	},

	emailTemplateCreatedDate: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'email_template',
			dateField: '_created_date',
			listKeys: ['email_templates'],
		}),
		queryParamName: 'emailTemplateCreatedDate',
		valueExtractor: {
			property: 'data',
		},
	},

	messagingTemplateKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name or description...',
			objectTable: '_messaging_template',
			listKeys: ['messaging_templates'],
			fulltextFields: ['name', 'description'],
		}),
		queryParamName: 'messagingTemplateKeywords',
	},

	messagingTemplateStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'messagingTemplateStatus',
		multiple: true,
		valueExtractor: {
			property: 'value',
		},
		options: [
			{ id: 'true', label: 'Active' },
			{ id: 'false', label: 'Inactive' },
		],
		queryMapping: {
			objectTable: '_messaging_template',
			fieldName: 'is_active',
			joinType: 'direct',
		},
		listKeys: ['messaging_templates'],
	},

	messagingTemplateCreatedDate: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: '_messaging_template',
			dateField: '_created_date',
			listKeys: ['messaging_templates'],
		}),
		queryParamName: 'messagingTemplateCreatedDate',
		valueExtractor: {
			property: 'data',
		},
	},

	// Client Emails Embedded List Filters
	clientEmailSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by subject or sender...',
			objectTable: 'email_record',
			listKeys: ['client-emails-embedded'],
			fulltextFields: ['subject', 'fromEmail', 'fromName'],
		}),
		queryParamName: 'search',
	},

	clientEmailDateRange: {
		...createDateRangeFilter({
			label: 'Date',
			objectTable: 'email_record',
			dateField: 'date',
			listKeys: ['client-emails-embedded'],
		}),
		queryParamName: 'dateRange',
		valueExtractor: {
			property: 'data',
		},
	},

	clientEmailSource: {
		type: 'single-select',
		label: 'Source',
		placeholder: 'All emails',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'sourceType',
		multiple: false,
		valueExtractor: {
			property: 'id',
		},
		options: [
			{ id: 'system', label: 'System Emails' },
			{ id: 'shared', label: 'Shared Emails' },
		],
		queryMapping: {
			objectTable: 'email_record',
			fieldName: 'sourceType',
			joinType: 'direct',
		},
		listKeys: ['client-emails-embedded'],
	},
};
